<template>
  <div>
    <VcNoticeModal :show-dialog="true"
                   :title-text="$t('file.missing_file_error.title')"
                   subtitle-text=""
                   :icon="require('@/assets/images/preview_empty_state.svg')"
                   :use-offset="false"
                   size="md"
                   @onCloseButtonClicked="!isLoading && $emit('close')">

      <template v-slot:content>
        <div class="subtitle"
             v-html="subtitleText"/>
      </template>

      <template v-slot:footer>
        <div class="infected-dialog-footer"
             :class="{'infected-dialog-footer--padding': !showError}">
          <div class="buttons-wrap">
            <label for="file-upload"
                   class="infected-dialog-footer__files-input-label"
                   :class="{'disabled': isLoading}"
                   v-if="canReUpload">
              <VcLoader v-if="isLoading"
                        color="white"
                        :size="20"/>
              <span v-else>{{$t('file.missing_file_error.replace_cta')}}</span>
            </label>

            <label @click="sendRequest"
                   class="infected-dialog-footer__files-input-label send-request-cta"
                   v-if="canSendRequest">
              <span>{{$t('file.missing_file_error.send_request_cta')}}</span>
            </label>

            <label v-if="!canReUpload && !canSendRequest"
                   @click="$emit('close')"
                   class="infected-dialog-footer__close-button">
              <span>{{$t('file.missing_file_error.close_cta')}}</span>
            </label>

            <input id="file-upload"
                   type="file"
                   @change="addFile($event.target.name, $event.target.files)">
          </div>

          <div v-if="$hasFeature('documents_corrupted')"
               @click="navigateToSupport"
               class="support-link">
            {{$t('file.missing_file_error.learn_more')}}
          </div>
        </div>

        <div v-if="showError"
             class="error-message">
          <VcAlert type="attention"
                   :header-text="$t('file.missing_file_error.error_header')"
                   :body-text="errorMessage"
                   :is-inline-header="true"/>
        </div>
      </template>

    </VcNoticeModal>

    <VcConfirmModal :show-dialog="showConfirmation"
                    size="sm"
                    :use-offset="false"
                    title=""
                    :content-text="$t('file.missing_file_error.confirmation_text')"
                    @onCloseButtonClicked="toggleConfirmation(false)"
                    @onCancelClicked="closeConfirmation"
                    @onOkClicked="upload"/>
  </div>
</template>

<script>
import fileReplaceService from "../services/fileReplaceService";
import {mapGetters} from "vuex";

export default {
  name: "InfectedFileDialog",
  props: {
    fileName: {
      type: String,
      required: true,
    },
    fileUid: {
      type: String,
      required: true,
    },
    isTemplate: {
      type: Boolean,
      required: true,
    },
    isClientInitiated: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      uploadedFile: null,
      showConfirmation: false,
      showError: false,
      errorMessage: '',
      isLoading: false
    };
  },
  created() {
    this.$frontageApi.listen('close-infected-file-dialog', this.closeDialog);
  },
  computed: {
    ...mapGetters("accountModule", ["account"]),
    subtitleText() {
      const params = {filename: this.fileName};

      if (this.canReUpload) {
        return this.canSendRequest ? this.$t('file.missing_file_error.subtitle_with_request', params) : this.$t('file.missing_file_error.subtitle', params);
      } else {
        return this.$t('file.missing_file_error.subtitle_upload_denied', params);
      }
    },
    canSendRequest(){
      return this.$hasFeature('documents_reupload_portal') && this.account.business.can_send_link && this.isClientInitiated;
    },
    maxAllowedSize() {
      return this.$hasFeature('can_upload_videos') ? Number.POSITIVE_INFINITY : this._mbToBytes(20);
    },
    canReUpload() {
      return this.$hasFeature('documents_reupload');
    },
    extensionErrorMessage() {
      return this.$t('file.missing_file_error.extension_error', {extension: this.fileExtension(this.fileName)});
    },
    sizeErrorMessage() {
      return this.$t('file.missing_file_error.size_error', {size: '20MB'});
    },
  },
  methods: {
    _mbToBytes(mb) {
      return mb * 1048576;
    },
    closeDialog(){
      this.$emit('close');
    },
    sendRequest(){
      this.$frontageApi.send('send-file-request')
    },
    navigateToSupport() {
      this.$frontageApi.openExternal('https://support.vcita.com/hc/en-us/articles/4429280386071-Important-update-regarding-documents-stored-in-vcita');
    },
    fileExtension(file) {
      return /(?:\.([^.]+))?$/.exec(file)[1].toLowerCase();
    },
    addFile(name, files) {
      this.isLoading = true;
      this.toggleShowError(false);

      const file = [...files][0];
      const extensionValidated = this.validateExtension(file);
      const sizeValidated = this.validateSize(file);
      const nameValidated = this.validateName(file);

      if (!extensionValidated) {
        this.toggleShowError(true);
        this.errorMessage = this.extensionErrorMessage;
        this.isLoading = false;
      } else if (!sizeValidated) {
        this.toggleShowError(true);
        this.errorMessage = this.sizeErrorMessage;
        this.isLoading = false;
      } else if (!nameValidated) {
        this.toggleConfirmation(true);
        this.uploadedFile = file;
      } else {
        this.uploadedFile = file;
        this.upload();
      }
    },
    validateName(file) {
      return file.name === this.fileName
    },
    validateExtension(file) {
      return this.fileExtension(file.name) === this.fileExtension(this.fileName)
    },
    validateSize(file) {
      return this.maxAllowedSize > file.size
    },
    toggleShowError(bool) {
      this.showError = bool;
    },
    toggleConfirmation(bool) {
      this.showConfirmation = bool;
    },
    closeConfirmation() {
      this.isLoading = false;
      this.toggleConfirmation(false);
    },
    upload() {
      this.toggleConfirmation(false);

      fileReplaceService.replaceFile(this.fileUid, this.uploadedFile, this.isTemplate)
        .then((res) => {
          if (![res.data?.error, res.data?.errors].some(value => [null, undefined].includes(value))) {
            this.$emit('failure');
          } else {
            this.$emit('success');
          }
        });
    }
  },
  destroyed() {
    this.$frontageApi.remove('close-infected-file-dialog', this.closeDialog);
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/abstract/_variables.scss";

.VcModalContainerContent {

  .subtitle {
    text-align: center;
  }
}

.infected-dialog-footer {
  width: 100%;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;

  &--padding {
    padding-bottom: 30px;
  }

  &__files-input-label, &__close-button {
    background-color: $brand_secondary_color;
    margin-bottom: 16px;
    border-radius: 47px;
    color: #fff;
    font-weight: 700;
    font-size: 13px;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    cursor: pointer;
    align-items: center;
    padding: 6px 0;
    border: 1px solid $brand_secondary_color;
  }

  .support-link {
    width: 100%;
    text-align: center;
    color: #6793f7;
    cursor: pointer;
    font-size: 13px;
    font-weight: 500;
  }
}

.buttons-wrap {
  padding-bottom: 10px;
  width: 212px;

  text-align: center;
  align-items: center;
  display: flex;
  flex-direction: column;

  .send-request-cta {
    color:$brand_secondary_color;
    background-color: white;
  }
}

input[type="file"] {
  display: none;
}

.error-message {
  padding-bottom: 30px;
}

.disabled {
  pointer-events: none;
}
</style>
